import { db, batchTransact, transactWithRetry } from '@/lib/db'
import { id } from '@instantdb/react'
import type { Project } from '@/types'
import { useProfile } from './useProfile'

/**
 * 项目管理 Hook
 * 提供项目的增删改查功能
 */
export function useProjects() {
  const { profile } = useProfile()
  
  // 查询企业内的项目（如果用户已绑定企业）
  const queryCondition = profile?.org_id
    ? { projects: { $: { where: { org_id: profile.org_id } } } }
    : { projects: {} }
  
  const { data, isLoading: dbLoading, error } = db.useQuery(queryCondition)

  const projects = (data?.projects || []) as Project[]

  // 创建项目
  const createProject = async (params: {
    title: string
    description?: string
    note_style_id: string
  }) => {
    const projectId = id()
    await transactWithRetry(
      db.tx.projects[projectId].update({
        id: projectId,
        org_id: profile?.org_id,  // 自动关联企业
        title: params.title,
        description: params.description || '',
        note_style_id: params.note_style_id,
        created_at: Date.now(),
      })
    )
    return projectId
  }

  // 更新项目
  const updateProject = async (
    projectId: string,
    updates: Partial<Omit<Project, 'id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.projects[projectId].update(updates))
  }

  // 删除项目（仅删除项目本身，不查询关联数据）
  // 级联删除请使用 useProjectDelete Hook
  const deleteProject = async (projectId: string) => {
    await transactWithRetry(db.tx.projects[projectId].delete())
  }

  return {
    projects,
    isLoading: dbLoading,
    error,
    createProject,
    updateProject,
    deleteProject,
  }
}

/**
 * 项目删除 Hook（带级联删除）
 * 在组件层面使用，可以访问实时订阅的关联数据
 */
export function useProjectDelete() {
  // 订阅所有素材和笔记（用于级联删除）
  const { data } = db.useQuery({
    assets: {},
    notes: {},
  })

  const deleteProjectWithCascade = async (projectId: string) => {
    const assets = (data?.assets || []) as any[]
    const notes = (data?.notes || []) as any[]

    // 过滤出该项目的关联数据
    const projectAssets = assets.filter((a) => a.project_id === projectId)
    const projectNotes = notes.filter((n) => n.project_id === projectId)

    // 构建删除事务（级联删除：项目 + 素材 + 笔记）
    const txns = [
      db.tx.projects[projectId].delete(),
      ...projectAssets.map((asset) => db.tx.assets[asset.id].delete()),
      ...projectNotes.map((note) => db.tx.notes[note.id].delete()),
    ]

    // 使用分批删除（避免超时）
    await batchTransact(txns)
  }

  return { deleteProjectWithCascade }
}

/**
 * 获取单个项目
 */
export function useProject(projectId: string) {
  const { data, isLoading: dbLoading, error } = db.useQuery({
    projects: {
      $: {
        where: { id: projectId },
      },
    },
  })

  const project = data?.projects?.[0] as Project | undefined

  return {
    project,
    isLoading: dbLoading,
    error,
  }
}
